import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ModuleLayout from '../components/ModuleLayout'
import Quiz from '../components/Quiz'
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Trophy,
} from 'lucide-react'

/* ─── Quiz Data ─── */
const QUIZ = [
  {
    question: 'Where does raw click data originally come from?',
    options: [
      'Google Sheets',
      'The client uploads it',
      'HasOffers tracking via BigQuery',
      'A manual spreadsheet entry',
    ],
    correctIndex: 2,
    explanation:
      'Click data flows from the website through HasOffers tracking, which logs it into BigQuery. From BigQuery, it is exported to the Clicks Sheet.',
  },
  {
    question: 'What formula combines the Clicks and Conversions sheets into one dataset?',
    options: [
      'IMPORTRANGE from both sheets',
      'VLOOKUP using a composite key (Offer ID & Date)',
      'CONCATENATE on all columns',
      'QUERY joining two ranges',
    ],
    correctIndex: 1,
    explanation:
      'VLOOKUP with a composite key (Offer ID concatenated with Date) looks up each click row in the Conversions sheet and brings the conversion count into a single combined table.',
  },
  {
    question: 'Why is a bridge sheet needed between master data and the client?',
    options: [
      'It makes formulas faster',
      'Google Sheets has a sharing limit',
      'It prevents the client from accessing other offers\' data via IMPORTRANGE',
      'It is required by BigQuery',
    ],
    correctIndex: 2,
    explanation:
      'If you IMPORTRANGE directly from master data into a client sheet, the client could modify the formula to access other offers\' data. The bridge sheet acts as a firewall, containing only the client\'s data.',
  },
  {
    question: 'When you FILTER the combined dataset by offer ID, what happens to rows that don\'t match?',
    options: [
      'They are hidden but still accessible',
      'They are deleted permanently',
      'They are excluded from the result — the original data is unchanged',
      'They move to a separate sheet',
    ],
    correctIndex: 2,
    explanation:
      'FILTER creates a new result set containing only matching rows. The original combined dataset remains completely intact — FILTER never modifies the source data.',
  },
  {
    question: 'What is the security risk of using IMPORTRANGE directly into a client-facing sheet?',
    options: [
      'The sheet becomes read-only',
      'The client gains access to the source sheet and could change the range to see other data',
      'IMPORTRANGE stops updating automatically',
      'The data becomes encrypted',
    ],
    correctIndex: 1,
    explanation:
      'When you share a sheet that has IMPORTRANGE, the recipient effectively gains access to the source data. They could edit the formula to pull different ranges, potentially seeing all offers\' confidential data.',
  },
  {
    question: 'What is a composite key?',
    options: [
      'A password for the spreadsheet',
      'Two or more columns combined to uniquely identify each row',
      'A formula that encrypts data',
      'A special Google Sheets feature for imports',
    ],
    correctIndex: 1,
    explanation:
      'A composite key combines two or more fields (like Offer ID + Date) to create a unique identifier for each row. Neither field alone is unique, but together they are.',
  },
  {
    question: 'How should you calculate CR% to handle days with zero clicks?',
    options: [
      '=Conversions/Clicks',
      '=IF(Clicks>0, Conversions/Clicks, "N/A")',
      '=IFERROR(Conversions/Clicks, 0)',
      '=SUM(Conversions)/SUM(Clicks)',
    ],
    correctIndex: 2,
    explanation:
      'IFERROR wraps the division so that if Clicks is 0 (causing a #DIV/0! error), the formula returns 0 instead of an error. This keeps the report clean and prevents broken cells.',
  },
  {
    question: 'In the full pipeline, how many sheets does the client have access to?',
    options: [
      '1 — only their client report sheet',
      '2 — the bridge and the client sheet',
      '3 — master, bridge, and client',
      '4 — clicks, conversions, bridge, and client',
    ],
    correctIndex: 0,
    explanation:
      'The client only ever accesses their client report sheet. The master sheet, clicks sheet, conversions sheet, and bridge sheet are all internal. The client never sees or knows about them.',
  },
  {
    question: 'What is the correct order of the full data pipeline?',
    options: [
      'BigQuery → HasOffers → Clicks Sheet → Combined → Client',
      'Website → HasOffers → BigQuery → Sheets → Combined → Bridge → Client',
      'Client → Bridge → Master → BigQuery',
      'Clicks Sheet → Conversions Sheet → BigQuery → Client',
    ],
    correctIndex: 1,
    explanation:
      'The pipeline flows: Website click → HasOffers SEED_COURSES it → BigQuery stores raw data → exported to Google Sheets (Clicks/Conversions) → combined with VLOOKUP → bridge filters one offer → client sheet imports from bridge.',
  },
  {
    question: 'Why should you avoid using merged cells in data tables?',
    options: [
      'They use more storage space',
      'They break FILTER, QUERY, VLOOKUP, and other formulas',
      'Google Sheets does not support merged cells',
      'They make the sheet load slower',
    ],
    correctIndex: 1,
    explanation:
      'Merged cells break nearly every data formula in Google Sheets. FILTER, QUERY, VLOOKUP, and sorting all rely on consistent row/column structure that merged cells destroy.',
  },
  {
    question: 'Why should you use cell references for offer IDs instead of hardcoding them in formulas?',
    options: [
      'Hardcoded values cause errors',
      'Cell references make formulas reusable — just change the cell value to update the whole report',
      'Google Sheets requires cell references',
      'Hardcoded values are slower to calculate',
    ],
    correctIndex: 1,
    explanation:
      'Putting offer IDs and other parameters in cells lets you update a report for a different client by changing one cell instead of editing every formula. This makes reports reusable and reduces errors.',
  },
  {
    question: 'Which of these is NOT an internal sheet in the pipeline?',
    options: [
      'Clicks Sheet',
      'Combined Dataset',
      'Bridge Sheet',
      'Client Report Sheet',
    ],
    correctIndex: 3,
    explanation:
      'The Client Report Sheet is the only sheet shared externally with the client. The Clicks Sheet, Conversions Sheet, Combined Dataset, and Bridge Sheet are all internal and never shared with clients.',
  },
]

/* ─── Pipeline stages ─── */
const PIPELINE_STAGES = [
  {
    label: 'Website',
    sub: 'User clicks ad',
    color: 'blue',
    module: null,
  },
  {
    label: 'HasOffers',
    sub: 'Tracks the click',
    color: 'blue',
    module: 0,
  },
  {
    label: 'BigQuery',
    sub: 'Stores raw data',
    color: 'blue',
    module: 0,
  },
  {
    label: 'Clicks Sheet',
    sub: 'Offer + Date + Clicks',
    color: 'green',
    module: 0,
  },
  {
    label: 'Conversions Sheet',
    sub: 'Postbacks / Reports',
    color: 'green',
    module: 0,
  },
  {
    label: 'Combined Dataset',
    sub: 'VLOOKUP join',
    color: 'purple',
    module: 9,
  },
  {
    label: 'Bridge Sheet',
    sub: 'FILTER + IMPORTRANGE',
    color: 'amber',
    module: 12,
  },
  {
    label: 'Client Report',
    sub: 'IMPORTRANGE from bridge',
    color: 'red',
    module: 10,
  },
]

/* ─── Recap steps ─── */
const RECAP_STEPS = [
  {
    num: 1,
    title: 'Raw data arrives',
    detail: 'Clicks flow from Website to HasOffers to BigQuery. Conversions arrive via postbacks or client CSV uploads.',
    module: 0,
  },
  {
    num: 2,
    title: 'Data lands in Google Sheets',
    detail: 'The Clicks Sheet has Offer ID + Date + Clicks. The Conversions Sheet has Offer ID + Date + Conversions.',
    module: 0,
  },
  {
    num: 3,
    title: 'Build the combined dataset',
    detail: 'Use VLOOKUP with a composite key (Offer ID & Date) to join clicks and conversions into one table. Add a CR% column with IFERROR.',
    module: 9,
  },
  {
    num: 4,
    title: 'Extract a per-offer report',
    detail: 'Use FILTER or QUERY on the combined dataset to pull one offer\'s rows. Format with clear headers, percentage formatting, and a totals row.',
    module: 10,
  },
  {
    num: 5,
    title: 'Understand IMPORTRANGE risks',
    detail: 'IMPORTRANGE grants the destination sheet access to the source. Never point it directly from master data into a client sheet.',
    module: 11,
  },
  {
    num: 6,
    title: 'Create the bridge sheet',
    detail: 'Use FILTER(IMPORTRANGE(...), condition) to pull only one offer\'s data from the master into an internal bridge sheet.',
    module: 12,
  },
  {
    num: 7,
    title: 'Create the client sheet',
    detail: 'Use IMPORTRANGE pointing at the bridge sheet. The client only sees their own data, even if they edit the formula.',
    module: 12,
  },
  {
    num: 8,
    title: 'Share safely',
    detail: 'Share only the client sheet with the client. Never share the master, combined dataset, or bridge sheets.',
    module: 12,
  },
]

/* ─── Common mistakes ─── */
const MISTAKES = [
  {
    title: 'IMPORTRANGE directly to client sheet',
    detail: 'Always use a bridge sheet in between. Direct IMPORTRANGE from master data lets clients access all your data.',
  },
  {
    title: 'Building manual tables instead of using FILTER',
    detail: 'FILTER dynamically pulls the right rows. Manual tables go stale when data changes and require constant maintenance.',
  },
  {
    title: 'Forgetting IFERROR on division',
    detail: 'Conversion Rate = Conversions/Clicks. If Clicks is 0, you get #DIV/0!. Always wrap in IFERROR.',
  },
  {
    title: 'Using merged cells in data tables',
    detail: 'Merged cells break FILTER, QUERY, VLOOKUP, and every other formula. Never merge cells in data ranges.',
  },
  {
    title: 'Hardcoding values instead of using cell references',
    detail: 'Put offer IDs and date ranges in cells, then reference those cells in formulas. This makes reports reusable and easy to update.',
  },
]

/* ─── Confetti ─── */
const CONFETTI_COLORS = [
  '#10b981', '#3b82f6', '#f59e0b', '#ef4444',
  '#8b5cf6', '#ec4899', '#14b8a6', '#f97316',
]

function Confetti() {
  const [particles] = useState(() =>
    Array.from({ length: 60 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 0.6,
      duration: 1.5 + Math.random() * 2,
      size: 6 + Math.random() * 8,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      rotation: Math.random() * 360,
      xDrift: (Math.random() - 0.5) * 120,
    }))
  )

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          initial={{ opacity: 1, y: -20, x: `${p.x}%`, rotate: 0 }}
          animate={{
            opacity: [1, 1, 0],
            y: ['0%', '120%'],
            x: `${p.x + (Math.random() - 0.5) * 30}%`,
            rotate: p.rotation + 360,
          }}
          transition={{
            delay: p.delay,
            duration: p.duration,
            ease: 'easeOut',
          }}
          style={{
            position: 'absolute',
            width: p.size,
            height: p.size,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            backgroundColor: p.color,
          }}
        />
      ))}
    </div>
  )
}

/* ─── Section card wrapper ─── */
function Section({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ delay, duration: 0.5, ease: 'easeOut' }}
      className="rounded-xl border border-slate-200 bg-white p-8"
    >
      {children}
    </motion.div>
  )
}

/* ─── Expandable Recap Step ─── */
function RecapStep({ step, index }) {
  const [open, setOpen] = useState(false)

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.06 }}
      className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden"
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-4 p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-700 font-bold text-sm shrink-0">
          {step.num}
        </span>
        <span className="flex-1 text-sm font-semibold text-gray-800">{step.title}</span>
        {step.module !== null && (
          <span className="text-xs text-gray-400 shrink-0 mr-2">Module {step.module}</span>
        )}
        {open ? (
          <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
        )}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pl-16">
              <p className="text-sm text-gray-600">{step.detail}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

/* ─── Main Module ─── */
export default function Module13() {
  return (
    <ModuleLayout moduleId={15}>
      <div className="space-y-8">
        {/* Section 1: Full Pipeline Visual */}
        <Section delay={0}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">The Full Pipeline</h2>
              <p className="text-lg text-gray-600">
                From a user clicking an ad to a client reading their report — this is the entire data flow.
              </p>
            </div>

            <div className="space-y-2">
              {PIPELINE_STAGES.map((stage, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="flex items-center gap-3"
                >
                  {i > 0 && (
                    <div className="flex items-center justify-center w-8 shrink-0">
                      <motion.div
                        initial={{ scaleY: 0 }}
                        whileInView={{ scaleY: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.08 }}
                        className="w-0.5 h-4 bg-gray-300 origin-top"
                      />
                    </div>
                  )}
                  {i > 0 && <div className="flex-1" />}
                </motion.div>
              ))}
            </div>

            {/* Horizontal pipeline */}
            <div className="overflow-x-auto pb-2">
              <div className="flex items-center gap-1 min-w-max px-2">
                {PIPELINE_STAGES.map((stage, i) => (
                  <div key={i} className="flex items-center">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className={`rounded-lg border-2 px-3 py-2 text-center min-w-[100px]
                        border-${stage.color}-300 bg-${stage.color}-50`}
                    >
                      <p className={`text-xs font-bold text-${stage.color}-800`}>{stage.label}</p>
                      <p className={`text-[10px] text-${stage.color}-600 mt-0.5`}>{stage.sub}</p>
                      {stage.module !== null && (
                        <p className="text-[9px] text-gray-400 mt-1">Module {stage.module}</p>
                      )}
                    </motion.div>
                    {i < PIPELINE_STAGES.length - 1 && (
                      <ArrowRight className="w-4 h-4 text-gray-300 shrink-0 mx-1" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* Section 2: Step-by-Step Recap */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Step-by-Step Recap</h2>
              <p className="text-lg text-gray-600">
                Click each step to see the details and which module covered it.
              </p>
            </div>

            <div className="space-y-2">
              {RECAP_STEPS.map((step, i) => (
                <RecapStep key={i} step={step} index={i} />
              ))}
            </div>
          </div>
        </Section>

        {/* Section 3: Common Mistakes */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Common Mistakes</h2>
              <p className="text-lg text-gray-600">
                Watch out for these. Every one of them causes real problems.
              </p>
            </div>

            <div className="space-y-3">
              {MISTAKES.map((mistake, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -15 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="flex items-start gap-4 rounded-xl border-2 border-amber-200 bg-amber-50 p-5"
                >
                  <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-amber-800">{mistake.title}</p>
                    <p className="text-sm text-amber-700 mt-1">{mistake.detail}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </Section>

        {/* Section 4: Final Quiz */}
        <Section delay={0.1}>
          <Quiz moduleId={15} questions={QUIZ} />
        </Section>
      </div>
    </ModuleLayout>
  )
}
